import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { Search, MapPin, Plus, MessageCircle, ShieldCheck, BadgeCheck, ArrowRight } from "lucide-react";
import Reveal, { MaskedLines } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import ListingCard from "@/components/ListingCard";
import { api, CATEGORIES, DISTRICTS, imgSrc } from "@/lib/api";
import { useSettings, DEFAULT_WA } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";

const HERO_IMG =
  "https://images.unsplash.com/photo-1637102272875-3e4986cd17b3?crop=entropy&cs=srgb&fm=jpg&q=85";
function Hero({ filters, setFilters, onSearch }) {
  const { user } = useAuth();
  const { settings } = useSettings();
  const wa = settings?.whatsapp_number || DEFAULT_WA;
  const line1 = settings?.hero_line1 || "İkinci Elde";
  const line2 = settings?.hero_line2 || "Zonguldak'ın";
  const line3 = settings?.hero_line3 || "Yeni Pazarı.";
  const heroImg = settings?.hero_image_url || HERO_IMG;
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const rx = useSpring(0, { stiffness: 200, damping: 25 });
  const ry = useSpring(0, { stiffness: 200, damping: 25 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    rx.set(((e.clientY - r.top) / r.height - 0.5) * -7);
    ry.set(((e.clientX - r.left) / r.width - 0.5) * 7);
  };

  return (
    <section ref={heroRef} className="relative overflow-hidden bg-emerald2 text-bone grain">
      <div className="pointer-events-none absolute -top-32 -right-32 h-[480px] w-[480px] rounded-full bg-coral/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -left-24 h-[420px] w-[420px] rounded-full bg-amber2/10 blur-3xl" />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 pt-28 md:pt-36 pb-16 md:pb-24 grid gap-12 lg:grid-cols-[1.1fr_0.9fr] items-center">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="mb-6 inline-flex items-center gap-2.5 rounded-full border border-bone/20 bg-bone/5 px-4 py-2 text-xs font-mono font-bold tracking-widest"
          >
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-coral opacity-75" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-coral" />
            </span>
            ZONGULDAK'IN YEREL 2. EL PAZARI
          </motion.div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black tracking-tight leading-[0.98]">
            <MaskedLines delay={0.25} lines={[line1, line2, <span key="c" className="text-coral">{line3}</span>]} />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.85 }}
            className="mt-6 max-w-lg text-base sm:text-lg text-bone/75 leading-relaxed"
          >
            {settings?.hero_subtitle || "İlanını ücretsiz ver, moderasyon ekibimiz onaylasın, alıcıyla doğrudan WhatsApp'tan anlaş. Aracısız, komisyonsuz, yerel."}
          </motion.p>
          <motion.form
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1 }}
            onSubmit={(e) => { e.preventDefault(); onSearch(); }}
            className="mt-8 flex flex-col sm:flex-row gap-2.5 max-w-xl"
          >
            <div className="relative flex-1">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="hero-search-input"
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                placeholder="Ne arıyorsun? Bisiklet, kanepe, iPhone..."
                className="w-full rounded-full border border-bone/20 bg-bone/10 py-3.5 pl-11 pr-4 text-sm text-bone placeholder:text-bone/40 outline-none focus:border-coral focus:bg-bone/15 transition"
              />
            </div>
            <select
              data-testid="district-filter-select"
              value={filters.district}
              onChange={(e) => setFilters({ ...filters, district: e.target.value })}
              className="rounded-full border border-bone/20 bg-emerald2 py-3.5 pl-4 pr-8 text-sm font-semibold text-bone outline-none focus:border-coral"
            >
              <option value="">Tüm Zonguldak</option>
              {DISTRICTS.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
            <button data-testid="hero-search-button" type="submit" className="rounded-full bg-coral px-7 py-3.5 text-sm font-bold text-white shadow-lg shadow-coral/30 hover:bg-coraldark transition">
              Ara
            </button>
          </motion.form>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.15 }}
            className="mt-6 flex flex-wrap items-center gap-3"
          >
            <button
              data-testid="hero-create-listing-button"
              onClick={() => navigate(user ? "/ilan-ver" : "/kayit")}
              className="inline-flex items-center gap-2 rounded-full bg-bone px-6 py-3.5 text-sm font-bold text-coal hover:bg-white hover:-translate-y-0.5 transition-all shadow-lg"
            >
              <Plus size={17} strokeWidth={3} className="text-coral" /> Ücretsiz İlan Ver
            </button>
            <a
              data-testid="whatsapp-hero-button"
              href={`https://wa.me/${wa}?text=${encodeURIComponent("Merhaba, alsat67.com hakkında bilgi almak istiyorum.")}`}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-wa/50 px-6 py-3.5 text-sm font-bold text-wa hover:bg-wa hover:text-white transition-all"
            >
              <MessageCircle size={17} /> WhatsApp Destek
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          onMouseMove={onMove}
          onMouseLeave={() => { rx.set(0); ry.set(0); }}
          style={{ perspective: 1000 }}
          className="relative hidden md:block"
        >
          <motion.div
            style={{ y: imgY, rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
            className="relative"
          >
            <div className="clip-frame overflow-hidden rounded-2xl border border-bone/15 shadow-2xl shadow-black/40">
              <img src={imgSrc(heroImg, 1600)} alt="Zonguldak sahil şehri" decoding="async" className="aspect-[4/3] w-full object-cover" />
            </div>
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              style={{ transform: "translateZ(50px)" }}
              className="absolute -bottom-5 -left-6 rounded-2xl bg-white p-3.5 pr-5 shadow-2xl"
            >
              <div className="flex items-center gap-3">
                <img src="https://images.unsplash.com/photo-1520549233664-03f65c1d1327?crop=entropy&cs=srgb&fm=jpg&q=85&w=120" alt="" className="h-12 w-12 rounded-xl object-cover" />
                <div>
                  <p className="text-[11px] font-semibold text-slate-400 flex items-center gap-1"><MapPin size={10} className="text-coral" /> Merkez, Zonguldak</p>
                  <p className="font-display text-lg font-black text-coal leading-tight">1.450 ₺</p>
                </div>
              </div>
            </motion.div>
            <div className="absolute -top-5 -right-4 flex items-center gap-2 rounded-2xl bg-emerald-600 px-4 py-3 shadow-2xl" style={{ transform: "translateZ(40px)" }}>
              <ShieldCheck size={18} className="text-white" />
              <span className="text-xs font-bold text-white">Onaylı İlan</span>
            </div>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 16, repeat: Infinity, ease: "linear" }}
              className="absolute -top-8 left-1/3 h-20 w-20 rounded-full bg-coral grid place-items-center text-center font-mono font-bold text-white text-xs leading-tight shadow-xl"
              style={{ transform: "translateZ(60px)" }}
            >
              ALSAT67<br />•67•<br />ZONGULDAK
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

export default function Home() {
  const { user } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const marqueeItems = (settings?.marquee_items || "").split("\n").filter(Boolean).length > 0
    ? (settings?.marquee_items || "").split("\n").filter(Boolean)
    : ["Zonguldak'ın İlk ve Tek Yerel 2. El Pazar Yeri", "Güvenli İlan Onayı", "Doğrudan WhatsApp", "%100 Ücretsiz İlan", "8 İlçede Hizmet"];
  const [filters, setFilters] = useState({ q: "", category: "", district: "" });
  const [listings, setListings] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (f = filters) => {
    setLoading(true);
    try {
      const params = {};
      if (f.q) params.q = f.q;
      if (f.category) params.category = f.category;
      if (f.district) params.district = f.district;
      const { data } = await api.get("/listings", { params });
      setListings(data);
    } catch (e) {
      setListings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.category, filters.district]);

  const onSearch = () => {
    const el = document.getElementById("ilanlar");
    if (el) (window.__lenis ? window.__lenis.scrollTo(el, { offset: -70 }) : el.scrollIntoView({ behavior: "smooth" }));
    load();
  };

  const steps = [
    { n: "01", t: "İlanını Ücretsiz Ver", d: "Fotoğraf, fiyat ve ilçeni ekle. 1 dakikada ilanın sisteme düşer, tamamen ücretsiz." },
    { n: "02", t: "Onaydan Geçsin", d: "Yetkili ekibimiz ilanı inceler; uygunsa anında yayınlanır, değilse neyi düzeltmen gerektiğini bildiririz." },
    { n: "03", t: "WhatsApp'tan Anlaş", d: "Alıcı 'WhatsApp' butonuna basar, görüşme doğrudan seninle kurulur. Aracı yok, komisyon yok." },
  ];

  return (
    <div>
      <Hero filters={filters} setFilters={setFilters} onSearch={onSearch} />

      <Marquee
        className="border-y-4 border-coal bg-amber2 py-3.5 text-coal"
        items={marqueeItems}
      />

      <section id="kategoriler" className="mx-auto max-w-7xl px-4 sm:px-6 pt-14 pb-6">
        <Reveal>
          <div className="flex items-end justify-between gap-4 mb-6">
            <div>
              <p className="font-mono text-xs font-bold tracking-widest text-coral">KATEGORİLER</p>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-1">Ne arıyorsun?</h2>
            </div>
          </div>
          <div className="flex gap-2.5 overflow-x-auto no-scrollbar pb-1 -mx-4 px-4 sm:mx-0 sm:px-0" data-testid="category-chips-row">
            {[{ id: "", name: "Tüm İlanlar" }, ...CATEGORIES].map((c) => (
              <button
                key={c.id}
                data-testid={`category-chip-${c.id || "all"}`}
                onClick={() => setFilters({ ...filters, category: c.id })}
                className={`shrink-0 rounded-full border px-5 py-2.5 text-sm font-bold transition-all ${
                  filters.category === c.id
                    ? "bg-coral border-coral text-white shadow-lg shadow-coral/25"
                    : "border-coal/15 bg-card hover:border-coral hover:text-coral"
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
        </Reveal>
      </section>

      <section id="ilanlar" className="mx-auto max-w-7xl px-4 sm:px-6 pb-20 scroll-mt-20">
        <Reveal>
          <div className="flex items-end justify-between gap-4 mb-7">
            <div>
              <p className="font-mono text-xs font-bold tracking-widest text-coral">PAZAR YERİ</p>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-1">Güncel İlanlar</h2>
            </div>
            {(filters.q || filters.category || filters.district) && (
              <button
                data-testid="clear-filters-button"
                onClick={() => setFilters({ q: "", category: "", district: "" })}
                className="text-sm font-semibold text-slate-500 hover:text-coral underline underline-offset-4"
              >
                Filtreleri temizle
              </button>
            )}
          </div>
        </Reveal>

        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="rounded-2xl border border-black/5 bg-card overflow-hidden animate-pulse">
                <div className="aspect-[4/3] bg-slate-200" />
                <div className="p-4 space-y-2.5">
                  <div className="h-3.5 w-4/5 rounded bg-slate-200" />
                  <div className="h-3 w-2/5 rounded bg-slate-100" />
                </div>
              </div>
            ))}
          </div>
        ) : listings && listings.length > 0 ? (
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5" data-testid="listing-feed-grid">
            {listings.map((l, i) => (
              <Reveal key={l.id} delay={Math.min(i * 0.05, 0.4)}>
                <ListingCard listing={l} />
              </Reveal>
            ))}
          </div>
        ) : (
          <div className="rounded-3xl border-2 border-dashed border-coal/15 bg-card/60 py-16 text-center" data-testid="empty-feed-state">
            <p className="font-display text-xl font-bold">Bu filtrede henüz ilan yok</p>
            <p className="mt-2 text-sm text-slate-500">İlk ilanı sen ver, Zonguldak'ta ilk ayak izini bırak.</p>
            <button
              data-testid="empty-feed-create-button"
              onClick={() => navigate(user ? "/ilan-ver" : "/kayit")}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-coral px-6 py-3 text-sm font-bold text-white hover:bg-coraldark transition"
            >
              İlan Ver <ArrowRight size={16} />
            </button>
          </div>
        )}
      </section>

      <section id="nasil-calisir" className="bg-emerald2 text-bone grain py-20 md:py-28 scroll-mt-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal>
            <p className="font-mono text-xs font-bold tracking-widest text-coral">3 ADIMDA ALSAT67</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mt-2 max-w-2xl">
              Yerel, güvenli, <span className="text-coral">yıldırım hızında.</span>
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {steps.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.12}>
                <div className="group h-full rounded-3xl border border-bone/15 bg-bone/5 p-7 hover:bg-bone/10 hover:-translate-y-1.5 transition-all duration-300" data-testid={`how-it-works-step-${i + 1}`}>
                  <span className="font-mono text-4xl font-bold text-coral">{s.n}</span>
                  <h3 className="mt-4 font-display text-xl font-bold">{s.t}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-bone/70">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-6 rounded-3xl bg-coral p-8 md:p-10">
              <div className="text-center sm:text-left">
                <h3 className="font-display text-2xl md:text-3xl font-black text-white">İlanın Zonguldak'ta görünsün.</h3>
                <p className="mt-2 text-sm text-white/85">Üye ol, ilanını ver — onaydan sonra tüm ilçede görünürsün.</p>
              </div>
              <div className="flex flex-wrap justify-center gap-3">
                <Link data-testid="cta-register-button" to={user ? "/ilan-ver" : "/kayit"} className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-bold text-coral hover:-translate-y-0.5 transition-all shadow-lg">
                  <BadgeCheck size={17} /> {user ? "İlan Ver" : "Üye Ol & İlan Ver"}
                </Link>
                <a data-testid="cta-whatsapp-button" href={`https://wa.me/${settings?.whatsapp_number || DEFAULT_WA}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border-2 border-white/40 px-7 py-3.5 text-sm font-bold text-white hover:bg-white/10 transition">
                  <MessageCircle size={17} /> Bize Sor
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
